import buildControllerStartDate from 'core/controllers/buildControllerStartDate'
import buildControllerEndDate from 'core/controllers/buildControllerEndDate'
import getValidEndDate from 'utils/pivot/date/getValidEndDate'
import setValueToSelect from 'utils/DOM/setValueToSelect'
import clickEventName from 'utils/DOM/clickEventName'
import { stringToDate } from '@hc-frontend/utils'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import textContent from 'utils/DOM/textContent'
import toggleClass from 'utils/DOM/toggleClass'
import store from 'utils/store/withMiddleware'
import getById from 'utils/DOM/getById'
import { findApplicant } from '@hc-frontend/business'
import isSureCarePlan from 'utils/pivot/plan/isSureCarePlan'
import getPeriodRange from 'core/controllerPlansFixed/utils/getPeriodRange'

let endDateWrapper
let periodCombo
let periodComboMulti
let localState
let startDateCtrl
let startDateCtrlMulti
let endDateCtrl
let isOnEdit

function onSave(loadPlans, multiCarrier) {
  cleanErrorMessage()
  const statusEnd = endDateCtrl.getStatus()
  const statusStart = multiCarrier
    ? startDateCtrlMulti.getStatus
    : startDateCtrl.getStatus()

  if (statusStart.valid && (!localState.showEndDate || statusEnd.valid)) {
    const state = store.getState()
    const hasPeriodChange = state.filters.period !== localState.period
    state.filters.period = localState.period
    const updateState = {
      filters: state.filters,
      sDate: localState.sDate,
      endDate: localState.endDate
    }

    if (hasPeriodChange) {
      state.filters.policies = state.minPolicies
    }

    if (state.sDate !== localState.sDate || state.endDate !== localState.endDate) {
      updateState.loading = true
      setTimeout(loadPlans, 0)
    }

    store.update(updateState)
    toggleEditableClasses()
    isOnEdit = false
  }
}

function toggleEditableClasses() {
  toggleClass('.coverage-period', 'edit')
  toggleClass(endDateWrapper, 'd-none', !localState.showEndDate)
}

function setErrorMessage(errorMessage: string): void {
  textContent('.coverage-period__error-message', errorMessage)
  toggleClass('.coverage-period__error-message', 'd-none', false)
}

function cleanErrorMessage(): void {
  textContent('.coverage-period__error-message', '')
  toggleClass('.coverage-period__error-message', 'd-none', true)
}

function validateStatus(): boolean {
  if (isOnEdit) {
    setErrorMessage('Save or Cancel the period change before proceeding')
    return false
  }

  return true
}

function handleOnBlurEndDate(status) {
  if (status.valid) {
    localState.endDate = status.input.value
  }
}

function handleOnBlurStartDate(status) {
  if (status.valid) {
    localState.sDate = status.input.value
    updateEndDate()
  }
}

function updateEndDate() {
  localState.endDate = getValidEndDate({
    startDate: stringToDate(localState.sDate),
    endDate: undefined,
    period: localState.period,
    isBTM: localState.isBTM,
    policies: localState.policies,
    dob: stringToDate(localState.dob)
  })

  endDateCtrl.setValue(localState.endDate)
}

function onPeriodChange() {
  localState.period = +this.value
  updateEndDate()
}

const shouldShowEndDate = ({ stateAcronym, isBTM, isSureCare, filters }): boolean => {
  if (isBTM) return false
  if (filters.policies !== 1) return false
  if (isSureCare && ['KS'].includes(stateAcronym)) return false

  return true
}

function updateForm() {
  const state = store.getState()
  const plan = state.plans[state.planId]
  const isBTM = isBTMPlan(plan)
  const isSureCare = isSureCarePlan(plan)
  const applicant = findApplicant(state.hcStorage.members)
  const stateAcronym = state.hcStorage.location.state_acronym

  if (plan?.multiCarrierPlan) updateOptions(['120'])
  if (isSureCare && stateAcronym === 'VA') updateOptions(['2x90'])
  const showEndDate = shouldShowEndDate({
    stateAcronym,
    isBTM,
    isSureCare,
    filters: state.filters
  })

  localState = {
    sDate: state.sDate,
    endDate: state.endDate,
    period: state.filters.period,
    showEndDate,
    isBTM,
    policies: state.filters.policies,
    dob: applicant.stringDOB
  }

  startDateCtrl.setValue(localState.sDate)
  if (startDateCtrlMulti) startDateCtrlMulti.setValue(localState.sDate)
  setValueToSelect(periodCombo, '' + localState.period)
  if (plan?.multiCarrierPlan) setValueToSelect(periodComboMulti, '' + localState.period)

  if (localState.showEndDate) {
    endDateCtrl.setValue(localState.endDate)
  }
}

function openEdit() {
  isOnEdit = true
  updateForm()
  toggleEditableClasses()
}

function onCancel() {
  cleanErrorMessage()
  isOnEdit = false
  toggleEditableClasses()
}

function updateOptions(durationIds) {
  periodCombo.options.length = 0
  for (const id of durationIds) {
    periodCombo.options[periodCombo.options.length] = new Option(id + ' Days', id)
  }

  if (periodComboMulti) {
    periodComboMulti.options.length = 0
    for (const id of durationIds) {
      periodComboMulti.options[periodComboMulti.options.length] = new Option(
        id + ' Days',
        id
      )
    }
  }
}

function getInstance(loadPlans, durationIds, state?, forceUpdate = false) {
  if (state) {
    const plan = state.plans[state.planId]
    const isMultiCarrier = plan?.multiCarrierPlan
    if (isMultiCarrier) {
      const multiCarrierOrder = Object.entries(state.multiCarrierOrder).map(
        order => order[1]
      )
      const relevantOrder = multiCarrierOrder.find(order => order.includes(plan.planId))
      const multiCarrierPlans = relevantOrder.map(order => state.plans[order])
      for (let i = 0; i < multiCarrierPlans.length; i++) {
        textContent(
          `.js-plan-end-date-${i}`,
          getPeriodRange(plan.durations[i].monthly.endDate).monthName.start
        )
        textContent(
          `.js-plan-month-end-${i}`,
          getPeriodRange(plan.durations[i].monthly.endDate).monthName.start
        )
      }
    }
  }

  if (!startDateCtrl) {
    endDateWrapper = getById('js-endDate')
    periodCombo = getById('period')
    periodComboMulti = getById('period-multi')
    updateOptions(durationIds)

    startDateCtrl = buildControllerStartDate({
      input: getById('sdate'),
      callOnValidate: handleOnBlurStartDate
    })
    startDateCtrl.isOnEdit = () => isOnEdit
    startDateCtrl.validateStatus = validateStatus

    if (getById('sdate-multi')) {
      startDateCtrlMulti = buildControllerStartDate({
        input: getById('sdate-multi'),
        callOnValidate: handleOnBlurStartDate
      })
      startDateCtrlMulti.isOnEdit = () => isOnEdit
      startDateCtrlMulti.validateStatus = validateStatus
    }

    endDateCtrl = buildControllerEndDate({ getStatus: () => localState })({
      input: getById('endDate'),
      callOnValidate: handleOnBlurEndDate
    })

    $('body')
      .on(clickEventName, '.coverage-period__edit', openEdit)
      .on('change', '#period', onPeriodChange)
      .on('change', '#period-multi', onPeriodChange)
      .on(clickEventName, '.coverage-period__save', () => onSave(loadPlans, false))
      .on(clickEventName, '.multi-save', () => onSave(loadPlans, true))
      .on(clickEventName, '.coverage-period__cancel', () => onCancel())
  } else if (forceUpdate) {
    updateOptions(durationIds)
  }

  return startDateCtrl
}

export default getInstance
